import React from 'react';
import Grid, { GridItem } from '@/components/layout/Grid';
import VBox from '@/components/layout/VBox';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import AlertBox from '@/components/layout/AlertBox';
import EditorCampos, { sugerirChave } from '@/components/EditorCampos';
import { CampoDataset, Dataset, camposAtivos } from '@tera/shared/src/types/Dataset';

type Props = {
    value?: Dataset | null;
    onChange?: (value: Dataset) => void;
    fieldErrors?: Record<string, string>;
};

const FormDataset = ({ value, onChange, fieldErrors }: Props) => {
    if (!value) return null;

    const novo = !value.id;
    const campos = camposAtivos({ campos: value.campos ?? [] });

    const chaveAcompanhaNome =
        novo && (!value.chave || value.chave === sugerirChave(value.nome ?? ''));

    const alterar = (mudanca: Partial<Dataset>) =>
        onChange?.({ ...value, ...mudanca } as Dataset);

    return (
        <Grid>
            <GridItem sm={12} md={6}>
                <CampoTexto
                    instanceId="dataset-nome"
                    label="Nome"
                    value={value.nome ?? ''}
                    onChange={(v: string) =>
                        alterar({
                            nome: v,
                            // A chave acompanha o nome enquanto for a sugestão automática
                            // e a tabela ainda não existir: depois de criada ela é imutável,
                            // porque fluxos e integrações referenciam a tabela por ela.
                            chave: chaveAcompanhaNome ? sugerirChave(v) : value.chave,
                        })
                    }
                    error={fieldErrors?.nome}
                />
            </GridItem>
            <GridItem sm={12} md={6}>
                <CampoTexto
                    instanceId="dataset-chave"
                    label="Chave"
                    value={value.chave ?? ''}
                    onChange={(v: string) => alterar({ chave: sugerirChave(v) })}
                    disabled={!novo}
                    error={fieldErrors?.chave}
                />
            </GridItem>

            <GridItem sm={12}>
                <CampoTexto
                    instanceId="dataset-descricao"
                    label="Descrição"
                    value={value.descricao ?? ''}
                    onChange={(v: string) => alterar({ descricao: v })}
                    multiline
                    error={fieldErrors?.descricao}
                />
            </GridItem>

            <GridItem sm={12}>
                <h3>Campos</h3>
                <p>
                    A <strong>chave</strong> identifica o campo nos registros já gravados e não
                    muda; o <strong>rótulo</strong> pode ser renomeado à vontade. Campos
                    marcados como <strong>buscáveis</strong> alimentam a busca livre.
                </p>

                <VBox gap={8} style={{ marginTop: '16px' }}>
                    <EditorCampos
                        // Remonta ao trocar de tabela, para o editor recalcular quais
                        // chaves já estão persistidas (e portanto são imutáveis).
                        key={value.id ?? 'novo'}
                        value={value.campos ?? []}
                        onChange={(cs: CampoDataset[]) => alterar({ campos: cs })}
                        fieldErrors={fieldErrors}
                    />
                </VBox>
            </GridItem>

            <GridItem sm={12} md={6}>
                <CampoMultiSeletor
                    instanceId="dataset-campos-chave"
                    label="Chave natural (identifica o registro no upsert)"
                    options={campos.map((c) => ({ id: c.chave, label: c.rotulo || c.chave }))}
                    value={value.campos_chave ?? []}
                    onChange={(v: string[]) => alterar({ campos_chave: v })}
                />
            </GridItem>
            <GridItem sm={12} md={6}>
                <CampoCheckbox
                    instanceId="dataset-busca-semantica"
                    label="Habilitar busca livre por significado"
                    value={!!value.busca_semantica}
                    onChange={(v: boolean) => alterar({ busca_semantica: v })}
                />
            </GridItem>

            {!value.campos_chave?.length && (
                <GridItem sm={12}>
                    <AlertBox variant="warning">
                        <p>
                            Sem chave natural, todo envio de registros insere linhas novas em vez
                            de atualizar as existentes.
                        </p>
                    </AlertBox>
                </GridItem>
            )}
        </Grid>
    );
};

export default FormDataset;
